import dayjs from 'dayjs'
import { AnyAction, combineReducers } from 'redux'
import { createSelector } from 'reselect'

import {
  ADD_JOURNAL_ENERGY_RECORD,
  ADD_JOURNAL_ENERGY_RECORD_FULFILLED,
  ADD_JOURNAL_ENERGY_RECORD_PENDING,
  ADD_JOURNAL_ENERGY_RECORD_REJECTED,
  FETCH_JOURNAL_ENERGY_RECORDS,
  FETCH_JOURNAL_ENERGY_RECORDS_FULFILLED,
  REMOVE_JOURNAL_ENERGY_RECORD,
  REMOVE_JOURNAL_ENERGY_RECORD_PENDING,
  REMOVE_JOURNAL_ENERGY_RECORD_REJECTED,
  UPDATE_JOURNAL_ENERGY_RECORD,
  UPDATE_JOURNAL_ENERGY_RECORD_FULFILLED
} from './actionTypes'
import { createPendingCounterReducer } from './common'

import { today } from 'Config'
import {
  JournalEnergyRecord,
  JournalEnergyRecordsReduxState,
  MealCategory,
  PromiseAction,
  ReduxStoreState,
  ThunkAction
} from 'Models'
import { currentJournalDateSelector } from 'ReduxStore/journalDay'
import { coreBackendService } from 'Services'
import { Utils } from 'Utils'

// Reducer
const data = (state: JournalEnergyRecord[] = [], { type, payload, meta }: AnyAction): JournalEnergyRecord[] => {
  switch (type) {
    case FETCH_JOURNAL_ENERGY_RECORDS_FULFILLED:
      return Utils.unique<JournalEnergyRecord>([...state, ...payload])
    case ADD_JOURNAL_ENERGY_RECORD_PENDING:
    case REMOVE_JOURNAL_ENERGY_RECORD_REJECTED:
      return [...state, meta.data]
    case ADD_JOURNAL_ENERGY_RECORD_FULFILLED: {
      return [...state, payload].filter((record: JournalEnergyRecord) => record.id !== meta.data.id)
    }
    case ADD_JOURNAL_ENERGY_RECORD_REJECTED:
    case REMOVE_JOURNAL_ENERGY_RECORD_PENDING:
      return state.filter((record: JournalEnergyRecord) => record.id !== meta.data.id)
    case UPDATE_JOURNAL_ENERGY_RECORD_FULFILLED:
      return state.map((record: JournalEnergyRecord) =>
        record.id === meta.data.id ? { ...record, ...meta.data } : record
      )
    default:
      return state
  }
}

// Actions
export const fetchJournalEnergyRecordsAction = (date: Date): PromiseAction<JournalEnergyRecord[]> => ({
  type: FETCH_JOURNAL_ENERGY_RECORDS,
  payload: coreBackendService.fetchJournalEnergyRecords({ date })
})

export const addJournalEnergyRecordAction = (
  description: string,
  energy: number,
  mealCategory: MealCategory,
  date: Date = today
): PromiseAction<JournalEnergyRecord> => ({
  type: ADD_JOURNAL_ENERGY_RECORD,
  payload: coreBackendService.addJournalEnergyRecord(description, energy, mealCategory, date),
  meta: {
    triggerDefaultSuccessToast: true,
    data: { id: `tmp-${Date.now()}`, description, energy, mealCategory, date }
  }
})

export const removeJournalEnergyRecordAction = (id: string): ThunkAction => async (dispatch, getState) => {
  const {
    journalEnergyRecords: { data: records }
  } = getState()

  const data = records.find((r) => r.id === id)

  if (data) {
    return dispatch({
      type: REMOVE_JOURNAL_ENERGY_RECORD,
      payload: coreBackendService.deleteJournalEnergyRecord(id),
      meta: {
        triggerDefaultSuccessToast: true,
        data
      }
    })
  }
}

export const updateJournalEnergyRecordAction = (
  newRecord: JournalEnergyRecord
): PromiseAction<JournalEnergyRecord> => ({
  type: UPDATE_JOURNAL_ENERGY_RECORD,
  payload: coreBackendService.updateJournalEnergyRecord(newRecord),
  meta: {
    triggerDefaultSuccessToast: true,
    data: { ...newRecord }
  }
})

// Selectors
export const journalEnergyRecordsSelector = ({
  journalEnergyRecords
}: ReduxStoreState): JournalEnergyRecordsReduxState => journalEnergyRecords

export const journalEnergyRecordsDataSelector = ({ journalEnergyRecords }: ReduxStoreState): JournalEnergyRecord[] =>
  journalEnergyRecords.data

export const dayJournalEnergyRecordsSelector = createSelector(
  [currentJournalDateSelector, journalEnergyRecordsDataSelector],
  (currentJournalDate, journalEnergyRecords) =>
    journalEnergyRecords.filter((journalRecord) =>
      journalRecord.datetime ? dayjs(currentJournalDate).isSame(journalRecord.datetime, 'day') : false
    )
)

const asyncActionTypes = [
  FETCH_JOURNAL_ENERGY_RECORDS,
  ADD_JOURNAL_ENERGY_RECORD,
  UPDATE_JOURNAL_ENERGY_RECORD,
  REMOVE_JOURNAL_ENERGY_RECORD
]

export default combineReducers<JournalEnergyRecordsReduxState>({
  data,
  pendingCounter: createPendingCounterReducer(...asyncActionTypes)
})
